/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define(['underscore',
	'./ConversionService'
], function(_, ConversionService) {
	const conversionServiceInstance = new ConversionService();
	const ConverterInterface = conversionServiceInstance.getInterface();
	const WIDGET_PROPERTIES_WHITELIST = [
		// area
		'area.interpolate',
		'data.handling',

		// bar
		'valueLabels.visible',

		//composite v2
		'column.valueLabels.visible',
		'lineWithPoints.valueLabels.visible',

		// line
		'valueAxis.zeroOrigin',
		'valueLabels.format',

		// pie
		'label.percentage',

		// radial
		'valueLabels.format',

		// stacked bar
		'stacked.percent',
		'valueLabels.visible',

		// summary
		'showItemLabel',

		//KPI
		'conditionalFormattingEnabled',
		'rangeScale',
		'numberOfRanges',
		'conditionalFormatting',

		// Crosstab
		'fixedRowHeight',
		'fixedColumnWidth',
		'columnWidths',
		'rowHeights'
	];
	const WIDGET_STYLES_BLACKLIST = ['fillColor', 'borderColor'];
	class DashboardToExploreConverter extends ConverterInterface {
		constructor(definition) {
			super(Object.assign({
				src: 'DASHBOARD',
				target: 'EXPLORE'
			}, definition));
			this.isStylesStripped = false;
		}

		convert(src, target, spec) {
			return new Promise((resolve, reject) => {
				const specJson = JSON.parse(spec);
				if (!this.validate(specJson)) {
					reject(new Error('Invalid incoming spec'));
				}
				resolve(JSON.stringify(this._doConvert(specJson)));
			});
		}

		validate(spec) {
			return !!spec;
		}

		/**
		 *
		 * Get the content model properties from board layout spec
		 * @param {object} spec board layout spec
		 * @param {object} contentMap
		 * @example { 'model1': {'conditionalFormatting': {}}, 'model2': {'properties' : {}}}
		 */
		_getContentFromLayout(spec, contentMap) {
			if (!spec){
				return;
			}
			if(spec.content) {
				contentMap[spec.id] = spec.content;
			}
			if(spec.items && spec.items.length > 0) {
				spec.items.forEach(item => this._getContentFromLayout(item, contentMap));
			}
		}

		_getContentFromSpec(spec) {

			const contentMap = {};
			// spec returned from copy is slightly different from regular dashboard spec where layout is an array;
			// while in regular dashboard spec, layout is an object.
			if (_.isArray(spec.layout)){
				spec.layout.forEach((layout) => { this._getContentFromLayout(layout, contentMap); });
			}else{
				this._getContentFromLayout(spec.layout, contentMap);
			}
			return contentMap;
		}
		//convert dashboard fragment spec
		_doConvert(dashboardSpec) {
			//layout
			const cards = [];
			const widgetMap = {};
			const contentMap = this._getContentFromSpec(dashboardSpec);
			dashboardSpec.widgets.filter(widget => this._isSupported(widget)).forEach(widget => {
				const card = this._getCard(widget, contentMap);
				cards.push(card);
				this._stripoffWidgetStyles(widget);
				delete widget.titleHtml;
				delete widget.name;
				widget.showTitle = true;
				widgetMap[widget.id] = widget;
			});

			//copy datasources and others
			let exploreSpec = null;
			if (cards.length > 0) {
				exploreSpec = dashboardSpec;
				//At this point, for both launch and paste, dashboardSpecHelper.getContents() has already converted the applicable
				//pageContext (filter dock) filters to local filters in each widget spec.
				//Explore targets don't expose pageContext for single cards so we should not include it in the target spec.
				//(note: Ocassionally, dashboard to dashboard paste overrides this behaviour and uses pageContext filters directly.)
				exploreSpec.pageContext = [];
				exploreSpec.widgets = widgetMap;
				exploreSpec.layout = {
					id: _.uniqueId('exploreContainer'),
					items: cards,
					type: 'exploreContainer'
				};

				//Set a flag indicating that styles have been stripped off,
				//so that a toast message can be shown in Explore, once the
				//toast is shown, this flag will be removed.
				//TODO: This solution involves addtional manipulations on object
				//property,so there cound be a better way to show toast which gets
				//rid ofthe additional manipulations.
				if(this.isStylesStripped){
					exploreSpec.isStylesStripped = true;
					this.isStylesStripped = false;
				}
			}

			return exploreSpec;
		}

		/**
		 * Strip off styles of the widget
		 *
		 * @param {Object} widget - widget object
		 */
		_stripoffWidgetStyles(widget = {}) {
			// Filter out widget properties
			if(widget.hasOwnProperty('properties')) {
				const filterProperties = this._filterWidgetProperties(widget['properties']);
				if(filterProperties.length < widget['properties'].length) {
					if(filterProperties.length > 0) {
						widget['properties'] = filterProperties;
					} else {
						delete widget['properties'];
					}
					this.isStylesStripped = true;
				}
			}

			// Filter out widget styles
			WIDGET_STYLES_BLACKLIST.forEach((prop) => {
				if(widget.hasOwnProperty(prop)){
					delete widget[prop];
					if(!this.isStylesStripped){
						this.isStylesStripped = true;
					}
				}
			});
		}

		_filterWidgetProperties(properties) {
			return properties.filter( prop => WIDGET_PROPERTIES_WHITELIST.indexOf(prop.id) !== -1);
		}

		_getCard(widget, contentMap) {
			return {
				id: _.uniqueId('exploreCard'),
				type: 'exploreCard',
				items:[{
					id: widget.id,
					type: 'widget',
					content: contentMap[widget.id]
				}],
				isHidden: false,
				isFavourite: false
			};
		}

		_isSupported(widget) {
			const unsupportedVisIds = ['dataPlayer', 'customVis', 'schematic', 'visualizationPreview', 'com.ibm.vis.schematicsPreview'];
			return widget.type === 'live' &&
				unsupportedVisIds.find(id => id === widget.visId) === undefined;
		}
	}

	return DashboardToExploreConverter;
});
